import { useId } from "react";

import { tv } from "tailwind-variants";

import { cn, type VariantSlotsToClassNames } from "@virtual-brain-lib/frontend-design-system/utils";

import { Icon, type IconProps, type Icons } from "../Icon";
import { Span } from "../Span";
import { Tag } from "../Tag";
import { Tooltip } from "../Tooltip";

export interface ChoiceCardOption<TValue extends string = string> {
	readonly value: TValue;
	readonly title: string;
	readonly description: string;
	readonly icon?: Icons;
	readonly iconSize?: IconProps["size"];
	readonly iconCentered?: boolean;
	readonly iconLayout?: "top" | "left" | "inline";
	readonly iconBackgroundColor?: string;
	readonly titleCentered?: boolean;
	readonly badge?: string;
	readonly disabled?: boolean;
	readonly tooltip?: string;
}

export const choiceCardVariants = tv({
	slots: {
		wrapper:
			"relative flex flex-1 cursor-pointer rounded-lg border p-4 text-left transition-colors",
		badge: "rounded-xl",
		base: "flex flex-col",
	},
	variants: {
		selected: {
			true: {
				wrapper: "border-primary bg-primary-light",
			},
			false: {
				wrapper: "border-secondary-border hover:border-primary-border",
			},
		},
		orientation: {
			vertical: {
				wrapper: "flex-col gap-1",
				base: "h-full justify-between",
			},
			horizontal: {
				wrapper: "flex-row gap-4",
				badge: "absolute -top-3 right-0",
				base: "h-full",
			},
		},
		disabled: {
			true: {
				wrapper: "cursor-not-allowed opacity-60 hover:border-secondary-border",
			},
		},
	},
});

/**
 * Radio-group of selectable cards. Single select: clicking a card sets `value`.
 * Each option renders an optional icon, an optional badge (e.g. "Recommended"),
 * a title and a muted description.
 */
export function ChoiceCards<TValue extends string>({
	options,
	value,
	onChange,
	className,
	classNames,
}: {
	readonly options: ReadonlyArray<ChoiceCardOption<TValue>>;
	readonly value?: NoInfer<TValue>;
	readonly onChange: (value: TValue) => void;
	readonly className?: string;
	readonly classNames?: VariantSlotsToClassNames<typeof choiceCardVariants>;
}) {
	const tooltipIdPrefix = useId();

	return (
		<div role="radiogroup" className={cn("flex gap-3", className)}>
			{options.map((option) => {
				const selected = option.value === value;
				const orientation = option.iconLayout === "left" ? "horizontal" : "vertical";
				const disabled = option.disabled ?? false;
				const tooltipId = `${tooltipIdPrefix}-${option.value}`;
				const hasTooltip = Boolean(option.tooltip);
				const { wrapper, badge, base } = choiceCardVariants({
					selected,
					orientation,
					disabled,
				});
				const isIconInline = option.iconLayout === "inline";
				const title = (
					<Span
						isBlock
						variant="body-md"
						className={cn(
							!isIconInline && "mb-2",
							!isIconInline && orientation === "vertical" && "pt-2",
							option.titleCentered && "text-center",
						)}
					>
						{option.title}
					</Span>
				);

				return (
					<button
						key={option.value}
						type="button"
						role="radio"
						aria-checked={selected}
						aria-disabled={disabled}
						data-tooltip={hasTooltip ? tooltipId : undefined}
						className={wrapper({ className: classNames?.wrapper })}
						onClick={() => {
							if (!disabled) {
								onChange(option.value);
							}
						}}
					>
						{option.icon && orientation === "horizontal" && (
							<div
								className={cn(
									"flex size-10 shrink-0 items-center justify-center rounded-lg",
									option.iconBackgroundColor ?? "bg-primary",
								)}
							>
								<Icon name={option.icon} size="lg" color="default" />
							</div>
						)}
						<div className={base({ className: classNames?.base })}>
							{option.icon && orientation === "vertical" && (
								<div
									className={cn(
										"flex min-h-6 items-center",
										option.iconCentered ? "justify-center" : "justify-between",
										isIconInline && "mb-2",
									)}
								>
									<div className="flex items-center gap-2">
										<Icon
											name={option.icon}
											size={option.iconSize ?? "md"}
											color="default"
										/>
										{isIconInline && title}
									</div>
									{option.badge && (
										<Tag
											className={badge({ className: classNames?.badge })}
											color="success"
											size="sm"
											leftIcon="sparkles"
										>
											{option.badge}
										</Tag>
									)}
								</div>
							)}
							{!(isIconInline && option.icon) && title}
							<Span
								isBlock
								variant="body-sm"
								className={cn(
									"text-default/65",
									option.titleCentered && "text-center",
								)}
							>
								{option.description}
							</Span>
						</div>
						{option.badge && orientation === "horizontal" && (
							<Tag className={badge()} color="success" size="sm" leftIcon="sparkles">
								{option.badge}
							</Tag>
						)}
						{hasTooltip && <Tooltip anchor={tooltipId}>{option.tooltip}</Tooltip>}
					</button>
				);
			})}
		</div>
	);
}
